import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  bomConfigVersionId,
  type BomConfigVersionId,
} from './BomConfigVersion.js'

/** Identifier type for public.bom_part_ */
export type BomPartId = string & { __brand: 'BomPartId' }

/** Represents the table public.bom_part_ */
export default interface BomPartTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<BomPartId, BomPartId | undefined, BomPartId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.uuid */
  versionId: ColumnType<
    BomConfigVersionId,
    BomConfigVersionId,
    BomConfigVersionId
  >

  /** Database type: pg_catalog.text */
  category: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  key: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  itemName: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  itemCode: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  uom: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  qtyType: ColumnType<string, string | undefined, string>

  /** Database type: pg_catalog.numeric */
  qtyValue: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.numeric */
  weightPerUnit: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.numeric */
  weightPerSensor: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.int4 */
  shotCount: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.numeric */
  shotWeight: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.text */
  notes: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.bool */
  isActive: ColumnType<boolean | null, boolean | null, boolean | null>
}

export type BomPart = Selectable<BomPartTable>

export type BomPartInitializer = Insertable<BomPartTable>

export type BomPartMutator = Updateable<BomPartTable>

export const bomPartId = z.string() as unknown as z.Schema<BomPartId>

export const bomPart = z.object({
  id: bomPartId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
  versionId: bomConfigVersionId,
  category: z.string(),
  key: z.string(),
  itemName: z.string(),
  itemCode: z.string(),
  uom: z.string(),
  qtyType: z.string(),
  qtyValue: z.number().nullable().optional(),
  weightPerUnit: z.number().nullable().optional(),
  weightPerSensor: z.number().nullable().optional(),
  shotCount: z.number().nullable().optional(),
  shotWeight: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  isActive: z.boolean().nullable().optional(),
}) as unknown as z.Schema<BomPart>

export const bomPartInitializer = z.object({
  id: bomPartId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  versionId: bomConfigVersionId,
  category: z.string(),
  key: z.string(),
  itemName: z.string(),
  itemCode: z.string(),
  uom: z.string(),
  qtyType: z.string().optional(),
  qtyValue: z.number().optional().nullable(),
  weightPerUnit: z.number().optional().nullable(),
  weightPerSensor: z.number().optional().nullable(),
  shotCount: z.number().optional().nullable(),
  shotWeight: z.number().optional().nullable(),
  notes: z.string().optional().nullable(),
  isActive: z.boolean().optional().nullable(),
}) as unknown as z.Schema<BomPartInitializer>

export const bomPartMutator = z.object({
  id: bomPartId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  versionId: bomConfigVersionId.optional(),
  category: z.string().optional(),
  key: z.string().optional(),
  itemName: z.string().optional(),
  itemCode: z.string().optional(),
  uom: z.string().optional(),
  qtyType: z.string().optional(),
  qtyValue: z.number().optional().nullable(),
  weightPerUnit: z.number().optional().nullable(),
  weightPerSensor: z.number().optional().nullable(),
  shotCount: z.number().optional().nullable(),
  shotWeight: z.number().optional().nullable(),
  notes: z.string().optional().nullable(),
  isActive: z.boolean().optional().nullable(),
}) as unknown as z.Schema<BomPartMutator>
